import React, { useEffect, useState } from 'react'
import isMobile from 'is-mobile'
import useOutsideClick from '@helpers/hooks/useOutsideClick'
import useEscapeKey from '@helpers/hooks/useEscapeKey'
import { ReactComponent as ArrowDownLightIcon } from '@svgs/keyline/arrow-down.svg'
import List, { ListItem, ListProps } from './List'
import styles from './styles.module.scss'

type DropdownProps<T extends string> = {
  variant?: 'default' | 'reverse'
  classes?: string
  defaultValue: T
  selectedClasses?: string
  placeholder?: string
  renderItem?: (item: ListItem) => React.ReactNode
} & ListProps

function Dropdown<T extends string>({
  variant = 'default',
  classes = '',
  optionClasses = '',
  selectedClasses = '',
  defaultValue,
  placeholder,
  list,
  selection = null,
  setSelection,
  id,
  renderItem
}: DropdownProps<T>) {
  const [selectedItem, setSelectedItem] = useState<ListItem | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const ref = useOutsideClick<HTMLButtonElement>(() => setIsOpen(false))

  const handleClick = (event: React.MouseEvent | KeyboardEvent) => {
    event.stopPropagation()
    setIsOpen(!isOpen)
  }

  useEscapeKey(event => {
    if (isOpen) {
      handleClick(event)
    }
  })

  useEffect(() => {
    const selected = list.find(
      l => l.value.toString() === selection?.toString()
    )
    setSelectedItem(selected || null)
  }, [selection, list])

  const shouldShowNative = isMobile({ tablet: true })

  const isSelectionNotAvailable =
    list.find(l => l.value === selection)?.availability === false
      ? styles.dropdown__valueNotAvailable
      : ''

  if (shouldShowNative) {
    return (
      <section className={`${styles.dropdown} ${classes}`}>
        <select
          onChange={e => {
            setSelection({ [id]: e.target.value })
          }}
        >
          {placeholder && (
            <option
              value={defaultValue}
              selected={selectedItem?.label === defaultValue}
              disabled
            >
              {placeholder}
            </option>
          )}
          {list.map(item => {
            return (
              <option
                key={item.value}
                value={item.value}
                selected={selection === item.value}
                disabled={item.availability === false}
              >
                {item.label}
              </option>
            )
          })}
        </select>
        <ArrowDownLightIcon className={styles.arrowDown} />
      </section>
    )
  }

  return (
    <section className={`${styles.dropdown} ${classes} ${styles[variant]}`}>
      <button
        type="button"
        ref={ref}
        className={`${styles.dropdown__container} ${
          isOpen ? styles.isOpen : ''
        }`}
        onClick={handleClick}
        tabIndex={0}
        data-hide={!isOpen}
      >
        <p
          className={`${styles.dropdown__selected} ${
            selectedClasses || ''
          } ${isSelectionNotAvailable}`}
          data-value={id}
        >
          {renderItem && selectedItem
            ? renderItem(selectedItem)
            : selectedItem?.label || defaultValue}
        </p>
        <ArrowDownLightIcon className={styles.arrowDown} />
      </button>
      <ul
        className={`${styles.dropdown__list} dropdown-list`}
        data-hide={!isOpen}
        role="listbox"
      >
        <List
          list={list}
          selection={selection}
          setSelection={setSelection}
          optionClasses={optionClasses}
          id={id}
          renderItem={renderItem}
        />
      </ul>
    </section>
  )
}

export default Dropdown
